/**
 * PerformanceMonitor类 - 性能监控和动态优化
 * 
 * 职责:
 * - 监控帧率
 * - 检测性能瓶颈
 * - 动态调整渲染质量
 * - 限制粒子数量
 * 
 * 优化策略:
 * - FPS < 25: 降低粒子数量上限，禁用拖尾效果
 * - FPS < 40: 降低粒子分辨率，简化物理计算
 * - FPS > 55: 逐步恢复质量
 * - 移动设备默认使用中等质量
 * 
 * 验证需求: Requirements 1.4, 1.5, 7.3
 */
class PerformanceMonitor {
  constructor() {
    // FPS监控
    this.fps = 60;
    this.frameCount = 0;
    this.lastTime = performance.now();
    this.fpsUpdateInterval = 1000; // 每秒更新一次FPS
    this.fpsHistory = [];
    this.fpsHistorySize = 10;
    
    // 质量级别
    this.qualityLevel = 'high'; // 'low', 'medium', 'high'
    this.targetFPS = 60;
    
    // 性能阈值
    this.thresholds = {
      critical: 25,  // FPS < 25: 严重性能问题
      low: 40,       // FPS < 40: 性能较低
      good: 55       // FPS > 55: 性能良好
    };
    
    // 质量调整冷却时间
    this.adjustmentCooldown = 0;
    this.adjustmentCooldownDuration = 3; // 3秒
    
    // 检测移动设备
    this.isMobile = this._detectMobile();
    
    // 移动设备默认中等质量
    if (this.isMobile) {
      this.qualityLevel = 'medium';
      this.targetFPS = 30;
    }
  }

  /**
   * 检测是否为移动设备
   * @returns {boolean}
   * @private
   */
  _detectMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  /**
   * 更新性能监控
   * @param {number} deltaTime - 时间增量（秒）
   */
  update(deltaTime) {
    const currentTime = performance.now();
    this.frameCount++;
    
    // 更新FPS
    const elapsed = currentTime - this.lastTime;
    if (elapsed >= this.fpsUpdateInterval) {
      this.fps = Math.round((this.frameCount * 1000) / elapsed);
      
      // 添加到历史记录
      this.fpsHistory.push(this.fps);
      if (this.fpsHistory.length > this.fpsHistorySize) {
        this.fpsHistory.shift();
      }
      
      this.frameCount = 0;
      this.lastTime = currentTime;
    }
    
    // 更新冷却时间
    if (this.adjustmentCooldown > 0) {
      this.adjustmentCooldown -= deltaTime;
    }
  }

  /**
   * 获取当前FPS
   * @returns {number}
   */
  getFPS() {
    return this.fps;
  }

  /**
   * 获取平均FPS
   * @returns {number}
   */
  getAverageFPS() {
    if (this.fpsHistory.length === 0) return this.fps;
    
    const sum = this.fpsHistory.reduce((a, b) => a + b, 0);
    return Math.round(sum / this.fpsHistory.length);
  }

  /**
   * 检查是否应该降低质量
   * @returns {boolean}
   */
  shouldReduceQuality() {
    if (this.adjustmentCooldown > 0) return false;
    if (this.qualityLevel === 'low') return false;
    
    const avgFPS = this.getAverageFPS();
    
    // 根据当前质量级别判断
    if (this.qualityLevel === 'high' && avgFPS < this.thresholds.low) {
      return true;
    }
    
    if (this.qualityLevel === 'medium' && avgFPS < this.thresholds.critical) {
      return true;
    }
    
    return false;
  }

  /**
   * 检查是否应该提升质量
   * @returns {boolean}
   */
  shouldIncreaseQuality() {
    if (this.adjustmentCooldown > 0) return false;
    if (this.qualityLevel === 'high') return false;
    if (this.isMobile) return false; // 移动设备不自动提升质量
    
    const avgFPS = this.getAverageFPS();
    
    // 只有在性能良好时才提升质量
    if (avgFPS > this.thresholds.good) {
      return true;
    }
    
    return false;
  }

  /**
   * 降低质量级别
   */
  reduceQuality() {
    if (this.qualityLevel === 'high') {
      this.qualityLevel = 'medium';
      console.log('Performance: Reduced quality to medium');
    } else if (this.qualityLevel === 'medium') {
      this.qualityLevel = 'low';
      console.log('Performance: Reduced quality to low');
    }
    
    this.adjustmentCooldown = this.adjustmentCooldownDuration;
  }

  /**
   * 提升质量级别
   */
  increaseQuality() {
    if (this.qualityLevel === 'low') {
      this.qualityLevel = 'medium';
      console.log('Performance: Increased quality to medium');
    } else if (this.qualityLevel === 'medium') {
      this.qualityLevel = 'high';
      console.log('Performance: Increased quality to high');
    }
    
    this.adjustmentCooldown = this.adjustmentCooldownDuration;
  }

  /**
   * 获取当前质量级别
   * @returns {string}
   */
  getQualityLevel() {
    return this.qualityLevel;
  }

  /**
   * 设置质量级别
   * @param {string} level - 质量级别 ('low', 'medium', 'high')
   */
  setQualityLevel(level) {
    if (['low', 'medium', 'high'].includes(level)) {
      this.qualityLevel = level;
      this.adjustmentCooldown = this.adjustmentCooldownDuration;
    }
  }

  /**
   * 获取质量配置
   * @returns {Object}
   */
  getQualityConfig() {
    switch (this.qualityLevel) {
      case 'low':
        return {
          maxParticles: 2000,
          enableTrails: false,
          particleSize: 0.8,
          physicsQuality: 'low'
        };
      case 'medium':
        return {
          maxParticles: 3500,
          enableTrails: false,
          particleSize: 1.0,
          physicsQuality: 'medium'
        };
      case 'high':
        return {
          maxParticles: 5000,
          enableTrails: true,
          particleSize: 1.0,
          physicsQuality: 'high'
        };
      default:
        return this.getQualityConfig.call({ qualityLevel: 'medium' });
    }
  }

  /**
   * 检查性能是否严重下降
   * @returns {boolean}
   */
  isCriticalPerformance() {
    return this.getAverageFPS() < this.thresholds.critical;
  }

  /**
   * 获取性能统计信息
   * @returns {Object}
   */
  getStats() {
    return {
      fps: this.fps,
      averageFPS: this.getAverageFPS(),
      qualityLevel: this.qualityLevel,
      isMobile: this.isMobile,
      targetFPS: this.targetFPS
    };
  }

  /**
   * 重置监控器
   */
  reset() {
    this.fps = 60;
    this.frameCount = 0;
    this.lastTime = performance.now();
    this.fpsHistory = [];
    this.adjustmentCooldown = 0;
  }
}

export default PerformanceMonitor;
